import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { BreadcrumbSchema, LocalBusinessSchema, GoogleMapsSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { businessInfo } from '../data/mock';
import { seoConfig } from '../data/seoConfig';
import { submitLead } from '../lib/leads';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitLead({ ...formData, source: 'contact_page', page: '/contact' });
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you shortly.");
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      toast.error('Could not send your request. Please call ' + businessInfo.phone);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Contact', url: 'https://basinplumbing.com/contact/' }
  ];

  return (
    <>
      <Seo {...seoConfig.contact} />
      <BreadcrumbSchema items={breadcrumbs} />
      <LocalBusinessSchema />
      <GoogleMapsSchema />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Contact</span>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Need a plumber? We're here to help 24/7. Get in touch for a free estimate 
              or schedule your service today.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info & Map */}
              <div>
                <h2 className="text-2xl font-bold text-[#1a1a1a] mb-8">Get In Touch</h2>
                
                <div className="space-y-6 mb-8">
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#e85a24] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Phone className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1a1a1a] mb-1">Phone</h3>
                          <a 
                            href={`tel:${businessInfo.phone}`}
                            className="text-2xl text-[#e85a24] font-bold hover:underline"
                          >
                            {businessInfo.phone}
                          </a>
                          <p className="text-sm text-gray-500 mt-1">24/7 Emergency Service Available</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#1a1a1a] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1a1a1a] mb-1">Email</h3>
                          <a 
                            href={`mailto:${businessInfo.email}`}
                            className="text-[#1a1a1a] hover:text-[#e85a24] transition-colors"
                          >
                            {businessInfo.email}
                          </a>
                          <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#1a1a1a] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1a1a1a] mb-1">Hours</h3>
                          <p className="text-gray-700 font-medium">24/7 Emergency Service</p>
                          <p className="text-sm text-gray-500 mt-1">We're always available when you need us</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Google Maps Embed */}
                <Card className="border-0 shadow-xl overflow-hidden">
                  <CardContent className="p-0">
                    <div className="p-4 bg-[#1a1a1a] text-white">
                      <div className="flex items-start gap-3">
                        <MapPin className="text-[#e85a24] flex-shrink-0 mt-1" size={20} />
                        <div>
                          <h3 className="font-semibold mb-1">Our Location</h3>
                          <p className="text-gray-300 text-sm">{businessInfo.address.full}</p>
                        </div>
                      </div>
                    </div>
                    <iframe
                      title="Basin Plumbing Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3382.8!2d-102.4252!3d31.8673!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86fbc7c0baf766d5%3A0x9959fe1a1d46471!2sBasin%20Plumbing!5e0!3m2!1sen!2sus!4v1707847200000!5m2!1sen!2sus"
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                    <div className="p-4 bg-white border-t">
                      <a 
                        href="https://www.google.com/maps/dir//Basin+Plumbing,+3730+Kermit+Hwy,+Odessa,+TX+79764"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#e85a24] hover:text-[#d14a14] font-medium transition-colors"
                      >
                        <Navigation size={18} />
                        Get Directions on Google Maps
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <div>
                <Card className="border-0 shadow-xl">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">Request a Quote</h2>
                    <p className="text-gray-600 mb-8">
                      Fill out the form and we'll get back to you as soon as possible.
                    </p>

                    {submitted ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="text-green-600" size={32} />
                        </div>
                        <h3 className="text-xl font-semibold text-[#1a1a1a] mb-2">Message Sent!</h3>
                        <p className="text-gray-600">We'll get back to you within 24 hours.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Full Name *
                            </label>
                            <Input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              placeholder="John Smith"
                              className="w-full"
                              data-testid="contact-name-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone Number *
                            </label>
                            <Input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              placeholder="(432) 555-0123"
                              className="w-full"
                              data-testid="contact-phone-input"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="john@example.com"
                            className="w-full"
                            data-testid="contact-email-input"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Service Needed
                          </label>
                          <select
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#e85a24] focus:border-transparent"
                          >
                            <option value="">Select a service...</option>
                            <option value="water-heater">Water Heater</option>
                            <option value="drain-cleaning">Drain Cleaning</option>
                            <option value="gas-line">Gas Line</option>
                            <option value="sewer-line">Sewer Line</option>
                            <option value="trenchless">Trenchless Plumbing</option>
                            <option value="waterline">Waterline</option>
                            <option value="residential">General Residential</option>
                            <option value="emergency">Emergency Service</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message (optional)
                          </label>
                          <Textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your plumbing issue..."
                            rows={4}
                            className="w-full"
                          />
                        </div>

                        <Button 
                          type="submit" 
                          disabled={submitting}
                          className="w-full bg-[#e85a24] hover:bg-[#d14a14] text-white py-6 text-lg"
                          data-testid="contact-submit-button"
                        >
                          <Send className="mr-2" size={20} />
                          {submitting ? 'Sending...' : 'Send Request'}
                        </Button>

                        <p className="text-xs text-gray-500 text-center">
                          By submitting this form, you agree to be contacted by Basin Plumbing.
                        </p>
                      </form>
                    )}
                  </CardContent>
                </Card>

                {/* Emergency Call-out */}
                <Card className="mt-6 border-0 shadow-lg bg-[#e85a24] text-white">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-2">Plumbing Emergency?</h3>
                    <p className="text-white/90 mb-4">
                      Don't wait for a callback. Call us now for immediate assistance.
                    </p>
                    <a href={`tel:${businessInfo.phone}`}>
                      <Button className="bg-white text-[#e85a24] hover:bg-gray-100 w-full">
                        <Phone className="mr-2" size={18} />
                        Call {businessInfo.phone}
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-6 text-center">Areas We Serve</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {businessInfo.serviceAreas.map((area) => (
                <span 
                  key={area}
                  className="px-4 py-2 bg-gray-100 rounded-full text-gray-700 font-medium hover:bg-[#e85a24] hover:text-white transition-colors"
                >
                  {area}, TX
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ContactPage;
